import {
  addDays,
  endOfMonth,
  endOfWeek,
  isSameDay,
  startOfYear,
  subDays,
  subMonths,
} from 'date-fns'
import { isNil } from 'lodash'

import { Option, OptionsList } from 'modules/core/components/DateNavigator/_OptionsList'
import { FixedDuration } from 'modules/core/components/DateNavigator/constants'
import { Cycle } from 'modules/core/components/DateNavigator/cycleUtils'
import { calculateRegularRange } from 'modules/core/components/DateNavigator/dateUtils'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import i18n from 'modules/core/lib/i18n'

export type ShortcutOption =
  | 'day'
  | 'week'
  | 'month'
  | 'quarter'
  | 'year'
  | 'year_to_date'
  | 'custom'

const AVAILABLE_SHORTCUTS_BY_CYCLE: Record<Cycle, ShortcutOption[]> = {
  daily: ['day', 'week', 'month', 'quarter', 'year', 'custom'],
  weekly: ['week'],
  monthly: ['month', 'quarter', 'year'],
  quarterly: ['quarter', 'year'],
}

const isSameRange = (rangeA: [Date, Date], rangeB: [Date, Date]) => {
  return isSameDay(rangeA[0], rangeB[0]) && isSameDay(rangeA[1], rangeB[1])
}

const getNonRegularPeriod = (value: Exclude<ShortcutOption, 'custom'>): [Date, Date] => {
  const today = new Date()

  switch (value) {
    case 'day': {
      return [today, today]
    }

    case 'week': {
      const endDate = endOfWeek(today)
      return [subDays(endDate, 6), endDate]
    }

    case 'month': {
      const endDate = endOfMonth(today)
      return [addDays(endOfMonth(subMonths(endDate, 1)), 1), endDate]
    }

    case 'quarter': {
      const endDate = endOfMonth(today)
      return [addDays(endOfMonth(subMonths(endDate, 3)), 1), endDate]
    }

    case 'year': {
      const endDate = endOfMonth(today)
      return [addDays(endOfMonth(subMonths(endDate, 12)), 1), endDate]
    }

    case 'year_to_date': {
      return [startOfYear(today), today]
    }
  }
}

const getOptionLabel = (value: ShortcutOption, regularPeriod: boolean): string => {
  if (regularPeriod) {
    switch (value) {
      case 'day':
        return i18n.t('common.date_navigator.shortcuts.current_period.day')
      case 'week':
        return i18n.t('common.date_navigator.shortcuts.current_period.week')
      case 'month':
        return i18n.t('common.date_navigator.shortcuts.current_period.month')
      case 'quarter':
        return i18n.t('common.date_navigator.shortcuts.current_period.quarter')
      case 'year':
        return i18n.t('common.date_navigator.shortcuts.current_period.year')
      case 'year_to_date':
        return i18n.t('common.date_navigator.shortcuts.current_period.year_to_date')
    }
  }

  switch (value) {
    case 'day':
      return i18n.t('common.date_navigator.shortcuts.current_period.day')
    case 'week':
      return i18n.t('common.date_navigator.shortcuts.current_period.week')
    case 'month':
      return i18n.t('common.date_navigator.shortcuts.current_period.month')
    case 'quarter':
      return i18n.t('common.date_navigator.shortcuts.last_period.quarter')
    case 'year':
      return i18n.t('common.date_navigator.shortcuts.last_period.year')
    case 'year_to_date':
      return i18n.t('common.date_navigator.shortcuts.current_period.year_to_date')
    case 'custom':
      return i18n.t('common.date_navigator.shortcuts.custom')
  }
}

type Props = {
  regularPeriods?: FixedDuration[]

  cycle?: Cycle
  startDate: Date
  endDate: Date

  showCustomOption: boolean

  onDateRangeChange: (startDate: Date, endDate: Date) => void
  onCustomSelected: () => void
}

export const Shortcuts = ({
  regularPeriods,
  cycle,
  startDate,
  endDate,
  showCustomOption,
  onDateRangeChange,
  onCustomSelected,
}: Props) => {
  const options = useUnsafeMemo(() => {
    const currentOptions: Option<ShortcutOption>[] = []

    if (
      (!regularPeriods || regularPeriods.includes('day')) &&
      (!cycle || AVAILABLE_SHORTCUTS_BY_CYCLE[cycle].includes('day'))
    ) {
      currentOptions.push({
        label: getOptionLabel('day', !!regularPeriods),
        value: 'day',
      })
    }

    if (
      (!regularPeriods || regularPeriods.includes('week')) &&
      (!cycle || AVAILABLE_SHORTCUTS_BY_CYCLE[cycle].includes('week'))
    ) {
      currentOptions.push({
        label: getOptionLabel('week', !!regularPeriods),
        value: 'week',
      })
    }

    if (
      (!regularPeriods || regularPeriods.includes('month')) &&
      (!cycle || AVAILABLE_SHORTCUTS_BY_CYCLE[cycle].includes('month'))
    ) {
      currentOptions.push({
        label: getOptionLabel('month', !!regularPeriods),
        value: 'month',
      })
    }

    if (
      (!regularPeriods || regularPeriods.includes('quarter')) &&
      (!cycle || AVAILABLE_SHORTCUTS_BY_CYCLE[cycle].includes('quarter'))
    ) {
      currentOptions.push({
        label: getOptionLabel('quarter', !!regularPeriods),
        value: 'quarter',
      })
    }

    if (
      (!regularPeriods || regularPeriods.includes('year')) &&
      (!cycle || AVAILABLE_SHORTCUTS_BY_CYCLE[cycle].includes('year'))
    ) {
      currentOptions.push({
        label: getOptionLabel('year', !!regularPeriods),
        value: 'year',
      })
    }

    if (regularPeriods?.includes('year_to_date') && !cycle) {
      currentOptions.push({
        label: getOptionLabel('year_to_date', !!regularPeriods),
        value: 'year_to_date',
      })
    }

    if (showCustomOption) {
      currentOptions.push({
        label: getOptionLabel('custom', !!regularPeriods),
        value: 'custom',
      })
    }

    return currentOptions
  }, [regularPeriods, cycle, showCustomOption])

  const handleOptionSelected = useUnsafeCallback(
    (value: ShortcutOption) => {
      if (value === 'custom') {
        onCustomSelected()
        return
      }

      const newRange = isNil(regularPeriods)
        ? getNonRegularPeriod(value)
        : calculateRegularRange(value, new Date())

      onDateRangeChange(newRange[0], newRange[1])
    },
    [regularPeriods, onDateRangeChange, onCustomSelected]
  )

  const currentSelectedOption = useUnsafeMemo(
    () =>
      options.find((option) => {
        if (option.value === 'custom') {
          return false
        }

        const newRange = isNil(regularPeriods)
          ? getNonRegularPeriod(option.value)
          : calculateRegularRange(option.value, new Date())

        return isSameRange([startDate, endDate], newRange)
      })?.value ?? 'custom',
    [startDate, endDate, regularPeriods, options]
  )

  return (
    <OptionsList
      options={options}
      selectedOptionValue={currentSelectedOption}
      onOptionSelected={handleOptionSelected}
    />
  )
}
